// @ts-nocheck: converted from JS

import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoData from '@/components/NoData';
import TasksV1 from '@/components/Studies/Create/Steps/Tasks/TasksV1';
import { TasksV2 } from '@/components/Studies/Settings/Steps/Tasks/TasksV2';
import Banner from '@/components/UI/Banner';
import StepForm from '@/components/UI/StepForm';
import { isNotApproved, successToast } from '@/helpers';
import { BENCHLING_INTEGRATION_META_SLUG } from '@/helpers.constants.tsx';
import { _isNil, _notNil } from '@/littledash';
import type { StudyAlertV1 } from '@/model/Alert.model.ts';
import type { ID } from '@/model/Common.model';
import type { Study } from '@/model/Study.model';
import type { Endpoints } from '@/generated/internal-api/Endpoints';
import { type StudyFormMetaDetails } from '@/model/StudyForm.model';
import * as Auth from '@/support/auth';
import { ApiService } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useFetchEntity } from '@/support/Hooks/fetch';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { featuresSelector } from '@/support/Selectors.tsx';
import { createSelector } from '@reduxjs/toolkit';
import React, { type FC, useCallback, useEffect, useMemo, useReducer } from 'react';
import {
  RiFlaskFill,
  RiLayoutColumnFill,
  RiListCheck2,
  RiLock2Fill,
  RiNotification3Fill,
  RiPlayFill,
} from 'react-icons/ri';
import presetLockedStyles from './PresetLockedMessage.module.scss';
import { useSelector } from 'react-redux';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertsSettings } from '../Create/Steps/Alerts/AlertsSettings';
import BenchlingIntegration from '../Create/Steps/BenchlingIntegration';
import Information from '../Create/Steps/Information';
import SelectPreset from '../Create/Steps/SelectPreset';
import Team from '../Create/Steps/Team/Team';
import {
  DefaultFormProvider,
  generateInitialState,
  PresetFormProvider,
  reducer,
  UseFormProvider,
} from './Settings.utils';
import DangerZone from './Steps/DangerZone';
import Metadata from './Steps/Metadata';
import { isClosed } from '@/support/study.ts';
import { StudyCreateProvider } from '@/utils/studyCreation/StudyCreateProvider.tsx';
import { RouteService } from '@/support/RouteService';

interface SettingsStepFormProps {
  study?: Study;
  defaultProps: unknown;
}

const Settings = () => {
  const { id: studyId } = useParams();

  const {
    entity: study,
    fetchEntity: reloadStudy,
    entityLoading: studyLoading,
  } = useFetchEntity({
    entityType: 'study',
    params: { id: studyId },
    includes: 'metadata,study_groups,users,tasks,project,preset',
  });

  const [state, dispatch] = useReducer(reducer, {
    form: { loading: true, studyGroups: [] },
  });

  const loadAndInitialiseState = async () => {
    if (Auth.isWriteUserForStudy(study, false, false) || Auth.isCurrentTeamOwnerWithSettingsAccess()) {
      const initialState = generateInitialState(study);

      try {
        const taskTemplateResponse = await Http.get(apiRoute('tasks.templateGroups.show'));
        const presetResponse = await Http.get(apiRoute('study.presets'));
        let studyFormResponse;
        if (_notNil(study?.study_creation_template_id)) {
          studyFormResponse = await Http.get(
            apiRoute('team.studies.forms.get', { formId: study.study_creation_template_id })
          );
        }
        dispatch({
          type: 'initialiseState',
          data: {
            ...initialState,
            form: {
              ...initialState.form,
              templates: taskTemplateResponse.data.data,
              presets: presetResponse.data.data,
            },
            study: {
              ...initialState.study,
              studyForm: (studyFormResponse?.data?.data ?? {}) as StudyFormMetaDetails,
            },
          },
        });
      } catch (error) {
        dispatch({ type: 'SET_API_ERROR', data: error });
      } finally {
        dispatch({ type: 'SET_LOADING', data: false });
      }
    } else {
      dispatch({ type: 'SET_LOADING', data: false });
    }
  };

  useEffect(() => {
    if (_notNil(study) && !studyLoading) {
      loadAndInitialiseState();
    }
  }, [study, studyLoading]);

  useEffect(() => {
    if (state?.form?.submitting) {
      handlePatchStudy();
    }
  }, [state?.form?.submitting]);

  useEffect(() => {
    if (state?.form?.updatingBenchlingIntegration) {
      handlePatchBenchlingIntegration();
    }
  }, [state?.form?.updatingBenchlingIntegration]);

  const handlePatchStudy = async () => {
    try {
      const payload = {
        ...state.study,
      };
      delete payload.metadata;
      if (!(Auth.isAuthor(study) || Auth.isCurrentTeamOwnerWithSettingsAccess())) {
        // # Only authors and tenant admins with settings access can change study owner
        delete payload.owner;
      }
      await Http.patch(apiRoute('studies.update', study.id), payload);
      await reloadStudy();
      successToast('Successfully updated study.');
    } catch (error) {
      dispatch({ type: 'SET_API_ERROR', data: error });
    } finally {
      dispatch({ type: 'SET_SUBMITTING', data: false });
    }
  };

  const handlePatchBenchlingIntegration = async () => {
    try {
      const metadataValues = state.study.metadata.map((metadata) => ({
        glossary_id: String(metadata.glossary_id),
        value: metadata.value,
      }));

      const payload = {
        metadata: metadataValues,
      };
      await Http.patch(apiRoute('studies.update', study.id), payload);
      await reloadStudy();
      successToast('Successfully updated study.');
    } catch (error) {
      dispatch({ type: 'SET_API_ERROR', data: error });
    } finally {
      dispatch({ type: 'SET_UPDATING_BENCHLING_INTEGRATION', data: false });
    }
  };

  if (state.form.loading) {
    return (
      <div className="w-100 h-100">
        <Loading />
      </div>
    );
  }

  const studyNotApproved = isNotApproved(study);

  return (
    <>
      {Auth.isWriteUserForStudy(study, false, false) || Auth.isCurrentTeamOwnerWithSettingsAccess() ? (
        <>
          <div className="ph4 pt3">
            {state.form.archivedAt && (
              <Banner info className="mw6 mb4" dismiss={false}>
                <h3 className="f5 normal lh-title">This study has been completed</h3>
                <p className="f6 pt2 lh-copy">
                  Studies that have been completed can not change settings. You can resume a study in its danger zone.
                </p>
              </Banner>
            )}
            {state.form.canceledAt && (
              <Banner info className="mw6 mb4" dismiss={false}>
                <h3 className="f5 normal lh-title">This study has been cancelled</h3>
                <p className="f6 pt2 lh-copy">
                  Studies that have been cancelled can not change settings. You can resume a study in it&apos;s danger
                  zone.
                </p>
              </Banner>
            )}
            {state.form.error && (
              <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={state.form.error} />
            )}
            <SettingsStepForm
              study={study}
              defaultProps={{
                state,
                dispatch,
                handlePatchStudy,
                reloadStudy,
              }}
            />
          </div>
        </>
      ) : (
        <div className="w-100 h-100 flex items-center justify-center">
          <NoData
            title="You aren't authorized"
            text="Only team members with Write access have access to use Settings. The study author can update your permissions to give you access."
            link={`/studies/${studyId}${studyNotApproved ? '' : '/animals/'}`}
            btnTxt={`Return to ${studyNotApproved ? 'overview' : 'animals'}`}
          />
        </div>
      )}
    </>
  );
};

type PresetStatusResponse = Endpoints['GET /api/v1/studies/{studyApiId}/preset-status']['response'];
type PresetLockReason = PresetStatusResponse['reasons'][number];

interface PresetLockReasonConfig {
  label: string;
  icon: FC<{ size?: string }>;
  href?: (studyId: ID) => string;
}

const PRESET_LOCK_REASONS: Record<PresetLockReason, PresetLockReasonConfig> = {
  study_not_active: {
    label: "The study isn't active",
    icon: RiPlayFill,
    href: (studyId) =>
      RouteService.web({ route: 'studies.settings', path: { id: studyId }, query: { step: 'danger-zone' } }).route,
  },
  measurements_recorded: { label: 'Measurements have been recorded', icon: RiFlaskFill },
  alerts_added: {
    label: 'Alerts have been added',
    icon: RiNotification3Fill,
    href: (studyId) =>
      RouteService.web({ route: 'studies.settings', path: { id: studyId }, query: { step: 'alerts' } }).route,
  },
  measurement_tasks_added: {
    label: 'Measurement tasks have been added',
    icon: RiListCheck2,
    href: (studyId) =>
      RouteService.web({ route: 'studies.settings', path: { id: studyId }, query: { step: 'tasks' } }).route,
  },
  measurement_columns_exist: {
    label: 'Measurement data table columns exist',
    icon: RiLayoutColumnFill,
    href: (studyId) => RouteService.web({ route: 'studies.dataTables.list', path: { id: studyId } }).route,
  },
};

interface PresetLockedMessageProps {
  reasons: Array<PresetLockReason>;
  studyId: ID;
}

const PresetLockedMessage: FC<PresetLockedMessageProps> = ({ reasons, studyId }) => (
  <div className={`ui-card pa4 ${presetLockedStyles.container}`} data-testid="preset-locked-message">
    <div className="flex items-center mb3">
      <div className={`${presetLockedStyles.iconCircle} ${presetLockedStyles.headerIcon}`}>
        <RiLock2Fill size="1.25rem" />
      </div>
      <div className="ml3">
        <h3 className="f4 fw6 near-black mv0">Preset locked</h3>
        <p className="f6 mid-gray mv0 mt1">
          <span className="fw6">
            {reasons.length} {reasons.length === 1 ? 'item' : 'items'}
          </span>{' '}
          in the study {reasons.length === 1 ? 'is' : 'are'} blocking preset update. Clear{' '}
          {reasons.length === 1 ? 'it' : 'them'} to unlock.
        </p>
      </div>
    </div>
    <div className="bt b--moon-gray">
      {reasons.map((reason) => {
        const config = PRESET_LOCK_REASONS[reason];
        if (_isNil(config)) {
          return null;
        }
        const { label, icon: Icon, href } = config;
        const resolvedHref = href?.(studyId);
        return (
          <div key={reason} className={presetLockedStyles.reasonItem} data-testid={`preset-locked-reason--${reason}`}>
            {_notNil(resolvedHref) ? (
              <Link to={resolvedHref} className={`${presetLockedStyles.iconCircle} ${presetLockedStyles.clickable}`}>
                <Icon size="1rem" />
              </Link>
            ) : (
              <div className={presetLockedStyles.iconCircle}>
                <Icon size="1rem" />
              </div>
            )}
            <span className="f5 normal near-black">{label}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const features = createSelector([featuresSelector], (features) => ({
  benchlingFoldersEnabled: features.benchling_folders ?? false,
  tasksVersion: !(features?.hide_tasks ?? false) ? 'V2' : 'V1',
}));
const SettingsStepForm: React.FC<SettingsStepFormProps> = ({ study, defaultProps }) => {
  const { benchlingFoldersEnabled, tasksVersion } = useSelector(features);
  const [searchParams] = useSearchParams();
  const {
    response: alertsResponse,
    loading: loadingAlerts,
    invoke: reloadAlerts,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/alerts',
    path: { studyApiId: study.api_id },
    invokeOnInit: true,
  });
  const { response: presetStatusResponse, loading: loadingPresetStatus } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/preset-status',
    path: { studyApiId: study.api_id },
    options: { onError: { toast: false, slug: 'preset-status-load' } },
  });

  const alerts: Array<StudyAlertV1> = alertsResponse?.body ?? [];

  const informationOnSubmit = useCallback(
    async (data) => {
      const { tracking_day_base, ...rest } = data;
      try {
        if (tracking_day_base !== undefined) {
          await ApiService.call({
            endpoint: 'PATCH /api/v1/studies/{studyApiId}/settings',
            path: { studyApiId: study.api_id },
            body: { tracking_day_base },
            options: {
              onError: {
                toast: false,
                throw: true,
                capture: true,
                slug: 'study-settings-update-tracking-day-base',
              },
            },
          });
        }
        defaultProps.dispatch({ type: 'updateStudy', data: rest });
        defaultProps.dispatch({ type: 'SET_SUBMITTING', data: true });
      } catch (error) {
        defaultProps.dispatch({ type: 'SET_API_ERROR', data: error });
      }
    },
    [study.api_id, defaultProps.dispatch]
  );

  const steps = [
    {
      name: 'Information',
      slug: 'information',
      isValid: () => true,
      renderStep: () => (
        <UseFormProvider {...defaultProps} overrideOnSubmit={informationOnSubmit}>
          <Information {...defaultProps} settings />
        </UseFormProvider>
      ),
    },
    {
      name: 'Team',
      slug: 'team',
      isValid: () => true,
      renderStep: () => (
        <UseFormProvider {...defaultProps}>
          <Team {...defaultProps.state.study} author={defaultProps.state.form.author} />
        </UseFormProvider>
      ),
    },
    {
      name: 'Preset',
      slug: 'preset',
      isValid: () => true,
      renderStep: () => {
        if (loadingPresetStatus) {
          return <Loading txt="Loading Preset" />;
        }

        const presetStatus = presetStatusResponse?.body;
        if (_isNil(presetStatus)) {
          return <ApiErrorBanner errorType={ApiErrorType.FETCH} />;
        }

        if (presetStatus.locked) {
          return <PresetLockedMessage reasons={presetStatus.reasons} studyId={study.id} />;
        }

        return (
          <PresetFormProvider {...defaultProps}>
            <SelectPreset {...defaultProps} />
          </PresetFormProvider>
        );
      },
    },
    {
      name: 'Alerts',
      slug: 'alerts',
      isValid: () => true,
      renderStep: () =>
        loadingAlerts && _isNil(alerts) ? (
          <Loading txt="Loading Alerts" />
        ) : (
          <AlertsSettings study={study} alerts={alerts} reloadAlerts={() => reloadAlerts()} />
        ),
    },
    {
      name: 'Tasks',
      slug: 'tasks',
      isValid: () => true,
      renderStep: () => {
        switch (tasksVersion) {
          case 'V2':
            if (_notNil(study)) {
              return (
                <TasksV2
                  study={study}
                  createTaskEnabled={
                    (Auth.isWriteUserForStudy(study) || Auth.isCurrentTeamOwnerWithSettingsAccess()) && !isClosed(study)
                  }
                  reloadStudy={defaultProps.reloadStudy}
                />
              );
            }
            return null;
          default:
            return (
              <DefaultFormProvider {...defaultProps}>
                <TasksV1 {...defaultProps} />
              </DefaultFormProvider>
            );
        }
      },
    },
    {
      name: 'Metadata',
      slug: 'metadata',
      isValid: () => true,
      renderStep: () => <Metadata {...defaultProps} />,
    },
  ];

  const benchlingFolderMetaGlossary = useMemo(() => {
    return defaultProps?.state?.study?.studyForm?.sections?.reduce(
      (acc, section) => acc || section?.metadata?.find((meta) => meta.info.slug === BENCHLING_INTEGRATION_META_SLUG),
      null
    );
  }, [defaultProps?.state?.study?.studyForm]);

  const benchlingIntegrationOnSubmit = (data) => {
    defaultProps.dispatch({ type: 'updateStudy', data });
    defaultProps.dispatch({ type: 'SET_UPDATING_BENCHLING_INTEGRATION', data: true });
  };

  if (benchlingFoldersEnabled && _notNil(benchlingFolderMetaGlossary)) {
    steps.push({
      name: 'Benchling Integration',
      slug: 'benchling-integration',
      isValid: () => true,
      renderStep: () => (
        <UseFormProvider state={defaultProps.state} overrideOnSubmit={benchlingIntegrationOnSubmit}>
          <BenchlingIntegration
            state={defaultProps.state}
            dispatch={defaultProps.dispatch}
            metaglossary={benchlingFolderMetaGlossary}
            studyID={defaultProps.state.study.id}
            studyApiId={study?.api_id}
            isSaving={defaultProps.state.form.updatingBenchlingIntegration}
          />
        </UseFormProvider>
      ),
    });
  }

  steps.push({
    name: 'Danger zone',
    slug: 'danger-zone',
    isValid: () => true,
    renderStep: () => <DangerZone study={study} />,
  });

  const stepParam = searchParams.get('step');
  const outerStep = _notNil(stepParam) ? steps.findIndex((s) => s.slug === stepParam) : undefined;

  return (
    <StudyCreateProvider>
      <StepForm
        steps={steps}
        initialState={{ currentStep: 0, steps }}
        testPrefix="study-settings"
        disableNav
        disableOverflow
        titlePadding="pb0"
        outerStep={outerStep !== -1 ? outerStep : undefined}
      />
    </StudyCreateProvider>
  );
};

export default Settings;
